import { useState, useEffect, useRef } from 'react';
import { getBookedTimeSlotsApi } from '../../../util/api';

/**
 * Fetch booked time slots for a listing.
 * - aggregateBookedSlotsMap: all non-cancelled bookings (camp cards + fallback before date pick)
 * - bookedSlotsMap: bookings for the selected calendar day (time-slot availability)
 */
export const useBookedTimeSlots = (listingId, bookingDate) => {
  const [bookedSlotsMap, setBookedSlotsMap] = useState({});
  const [aggregateBookedSlotsMap, setAggregateBookedSlotsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const requestIdRef = useRef(0);

  const bookingDateString = bookingDate
    ? bookingDate instanceof Date
      ? bookingDate.toISOString().split('T')[0]
      : bookingDate
    : null;

  useEffect(() => {
    if (!listingId) {
      setBookedSlotsMap({});
      setAggregateBookedSlotsMap({});
      setLoading(false);
      setError(null);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const aggregateResponse = await getBookedTimeSlotsApi({
          listingId,
          sessionTypeId: null,
        });

        if (requestIdRef.current !== requestId) return;
        setAggregateBookedSlotsMap(aggregateResponse?.data || {});

        if (bookingDateString) {
          const dateResponse = await getBookedTimeSlotsApi({
            listingId,
            sessionTypeId: null,
            bookingDate: bookingDateString,
          });
          if (requestIdRef.current !== requestId) return;
          setBookedSlotsMap(dateResponse?.data || {});
        } else {
          setBookedSlotsMap({});
        }
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        console.error('Error fetching booked time slots:', err);
        setError('Failed to load booked time slots');
        setBookedSlotsMap({});
        setAggregateBookedSlotsMap({});
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [listingId, bookingDateString]);

  return {
    bookedSlotsMap,
    aggregateBookedSlotsMap,
    loading,
    error,
  };
};
